import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, getDocs, doc, setDoc, deleteDoc, query, orderBy } from 'firebase/firestore';
import Swal from 'sweetalert2';
import AdminLayout from './AdminLayout';
import SetupGuard from '../components/SetupGuard';

const ProductOptionsPage = () => {
  const [user, setUser] = useState(null);
  const [productOptions, setProductOptions] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingOption, setEditingOption] = useState(null);

  const navigate = useNavigate();
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        await loadData(currentUser.uid);
      } else {
        navigate('/admin/login');
      }
    });
    return () => unsubscribe();
  }, []);

  const loadData = async (uid) => {
    try {
      setLoading(true);
      const db = getFirestore();

      // Load product options
      const optionsSnapshot = await getDocs(
        query(collection(db, 'users', uid, 'productOptions'), orderBy('order'))
      );
      const opts = optionsSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setProductOptions(opts);

      // Load all products from all categories
      const categoriesSnapshot = await getDocs(
        query(collection(db, 'users', uid, 'categories'), orderBy('order'))
      );
      const cats = categoriesSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));

      const products = [];
      for (const cat of cats) {
        const prodsSnapshot = await getDocs(
          query(collection(db, 'users', uid, 'categories', cat.id, 'products'), orderBy('order'))
        );
        prodsSnapshot.docs.forEach(d => {
          products.push({ id: d.id, categoryId: cat.id, categoryName: cat.name, ...d.data() });
        });
      }
      setAllProducts(products);
    } catch (error) {
      console.error('Error loading data:', error);
      Swal.fire('Error', 'Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getProductsUsingOption = (option) => {
    return allProducts.filter(p => p.optionIds?.includes(option.id));
  };

  const handleDeleteOption = async (option) => {
    if (!user) return;

    const result = await Swal.fire({
      title: 'Delete Option?',
      text: `Are you sure you want to delete "${option.name}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      try {
        const db = getFirestore();
        await deleteDoc(doc(db, 'users', user.uid, 'productOptions', option.id));

        // Remove option id from all products using it
        const usingProducts = getProductsUsingOption(option);
        for (const product of usingProducts) {
          const newOptionIds = (product.optionIds || []).filter(id => id !== option.id);
          await setDoc(
            doc(db, 'users', user.uid, 'categories', product.categoryId, 'products', product.id),
            { optionIds: newOptionIds },
            { merge: true }
          );
        }

        await loadData(user.uid);
        window.dispatchEvent(new CustomEvent('setupGuideRefresh'));
        Swal.fire('Deleted', 'Option has been deleted', 'success');
      } catch (error) {
        console.error('Error deleting option:', error);
        Swal.fire('Error', 'Failed to delete option', 'error');
      }
    }
  };

  const handleSaveOption = async (formData) => {
    if (!user) return;

    try {
      const db = getFirestore();
      const optionRef = editingOption
        ? doc(db, 'users', user.uid, 'productOptions', editingOption.id)
        : doc(collection(db, 'users', user.uid, 'productOptions'));

      const data = {
        name: formData.name,
        optionRequired: formData.optionRequired,
        allowMultipleSelection: formData.allowMultipleSelection,
        order: parseInt(formData.order) || 0,
        usedInProductIds: formData.usedInProductIds || [],
        options: (formData.options || []).map(o => ({
          name: o.name,
          extraFee: parseFloat(o.extraFee) || 0,
        })),
        updatedAt: new Date(),
      };

      if (!editingOption) {
        data.createdAt = new Date();
      }

      await setDoc(optionRef, data, { merge: true });

      // Sync optionIds on products
      const oldUsedIds = editingOption?.usedInProductIds || [];
      const newUsedIds = formData.usedInProductIds || [];

      const removed = oldUsedIds.filter(id => !newUsedIds.includes(id));
      const added = newUsedIds.filter(id => !oldUsedIds.includes(id));

      for (const productId of removed) {
        const product = allProducts.find(p => p.id === productId);
        if (product) {
          const newOptionIds = (product.optionIds || []).filter(id => id !== optionRef.id);
          await setDoc(
            doc(db, 'users', user.uid, 'categories', product.categoryId, 'products', product.id),
            { optionIds: newOptionIds },
            { merge: true }
          );
        }
      }

      for (const productId of added) {
        const product = allProducts.find(p => p.id === productId);
        if (product) {
          const newOptionIds = [...(product.optionIds || [])];
          if (!newOptionIds.includes(optionRef.id)) {
            newOptionIds.push(optionRef.id);
          }
          await setDoc(
            doc(db, 'users', user.uid, 'categories', product.categoryId, 'products', product.id),
            { optionIds: newOptionIds },
            { merge: true }
          );
        }
      }

      setShowModal(false);
      setEditingOption(null);
      await loadData(user.uid);
      window.dispatchEvent(new CustomEvent('setupGuideRefresh'));
      Swal.fire('Success', editingOption ? 'Option updated' : 'Option created', 'success');
    } catch (error) {
      console.error('Error saving option:', error);
      Swal.fire('Error', 'Failed to save option', 'error');
    }
  };

  const filteredOptions = searchQuery
    ? productOptions.filter(o => o.name?.toLowerCase().includes(searchQuery.toLowerCase()))
    : productOptions;

  if (loading) {
    return (
      <AdminLayout title="Product Options">
        <div className="text-center py-5">
          <img src="/fodyfee_loading.png" alt="Loading..." style={{ width: 80, height: 80, objectFit: 'contain' }} />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Product Options">
      <SetupGuard prerequisites={['product']}>
      <div className="product-options-page">
        {/* Search and Add */}
        <div className="row mb-4">
          <div className="col-md-6">
            <div className="input-group">
              <div className="input-group-prepend">
                <span className="input-group-text"><i className="fas fa-search"></i></span>
              </div>
              <input
                type="text"
                className="form-control"
                placeholder="Search options..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <div className="input-group-append">
                  <button className="btn btn-outline-secondary" onClick={() => setSearchQuery('')}>
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="col-md-6 text-right">
            <button
              className="btn btn-primary"
              style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35' }}
              onClick={() => { setEditingOption(null); setShowModal(true); }}
            >
              <i className="fas fa-plus mr-2"></i>Add Option
            </button>
          </div>
        </div>

        {/* Options List */}
        {filteredOptions.length === 0 ? (
          <div className="text-center py-5">
            <i className="fas fa-sliders-h fa-4x text-muted mb-4"></i>
            <h4 className="text-muted">{searchQuery ? 'No options found' : 'No product options yet'}</h4>
          </div>
        ) : (
          <div className="row">
            {filteredOptions.map((option) => {
              const usingProducts = getProductsUsingOption(option);
              const isExpanded = expandedId === option.id;
              return (
                <div key={option.id} className="col-12 mb-3">
                  <div
                    className="card"
                    style={{ borderRadius: '16px', border: 'none', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}
                  >
                    <div
                      className="card-body"
                      style={{ cursor: 'pointer' }}
                      onClick={() => setExpandedId(isExpanded ? null : option.id)}
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                          <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'} text-muted mr-3`}></i>
                          <div>
                            <h5 className="font-weight-bold m-0" style={{ fontSize: '1.05rem' }}>{option.name}</h5>
                            <small className="text-muted">Used in {usingProducts.length} product{usingProducts.length !== 1 ? 's' : ''}</small>
                          </div>
                        </div>
                        <div className="d-flex align-items-center">
                          {option.optionRequired && (
                            <span className="badge badge-warning mr-2">Required</span>
                          )}
                          {option.allowMultipleSelection && (
                            <span className="badge badge-info mr-2">Multi</span>
                          )}
                          <span className="badge badge-light mr-3">{option.options?.length || 0} items</span>
                          <button
                            className="btn btn-sm btn-outline-primary mr-2"
                            onClick={(e) => { e.stopPropagation(); setEditingOption(option); setShowModal(true); }}
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={(e) => { e.stopPropagation(); handleDeleteOption(option); }}
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="mt-3 pt-3" style={{ borderTop: '1px solid #f0f0f0' }}>
                          {option.options && option.options.length > 0 ? (
                            <div className="row">
                              {option.options.map((item, idx) => (
                                <div key={idx} className="col-md-4 col-lg-3 mb-2">
                                  <div className="d-flex justify-content-between align-items-center p-2" style={{ background: '#f8f9fa', borderRadius: '8px' }}>
                                    <span style={{ fontSize: '0.9rem' }}>{item.name}</span>
                                    {item.extraFee > 0 && (
                                      <span className="badge badge-secondary">+${parseFloat(item.extraFee).toFixed(2)}</span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-muted mb-0">No items in this option</p>
                          )}

                          {usingProducts.length > 0 && (
                            <div className="mt-3">
                              <small className="text-muted font-weight-bold">Used in:</small>
                              <div className="mt-1">
                                {usingProducts.map(p => (
                                  <span key={p.id} className="badge badge-light mr-2 mb-1" style={{ fontSize: '0.8rem' }}>
                                    {p.name}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Option Modal */}
        {showModal && (
          <ProductOptionFormModal
            option={editingOption}
            allProducts={allProducts}
            onSave={handleSaveOption}
            onClose={() => { setShowModal(false); setEditingOption(null); }}
          />
        )}
      </div>
      </SetupGuard>
    </AdminLayout>
  );
};

const ProductOptionFormModal = ({ option, allProducts, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: option?.name || '',
    optionRequired: option?.optionRequired || false,
    allowMultipleSelection: option?.allowMultipleSelection || false,
    order: option?.order || 0,
    usedInProductIds: option?.usedInProductIds || [],
    options: option?.options || [],
  });
  const [showProductSelector, setShowProductSelector] = useState(false);
  const [productSearch, setProductSearch] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      Swal.fire('Error', 'Option name is required', 'error');
      return;
    }
    const items = Array.isArray(formData.options) ? formData.options : [];
    if (items.length === 0) {
      Swal.fire('Error', 'Add at least one option item (e.g. "Small", "Large")', 'error');
      return;
    }
    if (items.some((it) => !(it.name || '').toString().trim())) {
      Swal.fire('Error', 'Every option item needs a name', 'error');
      return;
    }
    onSave(formData);
  };

  const addOptionItem = () => {
    setFormData(prev => ({
      ...prev,
      options: [...prev.options, { name: '', extraFee: 0 }],
    }));
  };

  const removeOptionItem = (index) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index),
    }));
  };

  const updateOptionItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.map((item, i) => i === index ? { ...item, [field]: value } : item),
    }));
  };

  const toggleProduct = (productId) => {
    setFormData(prev => ({
      ...prev,
      usedInProductIds: prev.usedInProductIds.includes(productId)
        ? prev.usedInProductIds.filter(id => id !== productId)
        : [...prev.usedInProductIds, productId],
    }));
  };

  const filteredProducts = productSearch
    ? allProducts.filter(p => p.name?.toLowerCase().includes(productSearch.toLowerCase()))
    : allProducts;

  const selectedProducts = allProducts.filter(p => formData.usedInProductIds.includes(p.id));

  return (
    <>
      <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title font-weight-bold">{option ? 'Edit Option' : 'Add Option'}</h5>
              <button type="button" className="close" onClick={onClose}>
                <span>&times;</span>
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-8">
                    <div className="form-group">
                      <label>Option Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label>Order</label>
                      <input
                        type="number"
                        className="form-control"
                        value={formData.order}
                        onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-6">
                    <div className="custom-control custom-switch">
                      <input
                        type="checkbox"
                        className="custom-control-input"
                        id="optionRequired"
                        checked={formData.optionRequired}
                        onChange={(e) => setFormData({ ...formData, optionRequired: e.target.checked })}
                      />
                      <label className="custom-control-label" htmlFor="optionRequired">Required</label>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="custom-control custom-switch">
                      <input
                        type="checkbox"
                        className="custom-control-input"
                        id="allowMultipleSelection"
                        checked={formData.allowMultipleSelection}
                        onChange={(e) => setFormData({ ...formData, allowMultipleSelection: e.target.checked })}
                      />
                      <label className="custom-control-label" htmlFor="allowMultipleSelection">Allow Multiple Selection</label>
                    </div>
                  </div>
                </div>

                {/* Used In Products Section */}
                <div className="form-group">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="m-0 font-weight-bold">Used In Products</label>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => setShowProductSelector(true)}
                    >
                      {selectedProducts.length > 0 ? `${selectedProducts.length} Selected` : 'Select Products'}
                    </button>
                  </div>
                  {selectedProducts.length > 0 ? (
                    <div className="d-flex flex-wrap" style={{ gap: '6px' }}>
                      {selectedProducts.map(p => (
                        <span key={p.id} className="badge badge-light" style={{ fontSize: '0.85rem', padding: '6px 10px' }}>
                          {p.name}
                          <button
                            type="button"
                            className="btn btn-sm btn-link text-danger p-0 ml-2"
                            style={{ fontSize: '0.8rem', lineHeight: 1 }}
                            onClick={() => toggleProduct(p.id)}
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-3" style={{ background: '#f8f9fa', borderRadius: '12px' }}>
                      <p className="text-muted mb-0">No products selected</p>
                    </div>
                  )}
                </div>

                {/* Options/Items List */}
                <div className="form-group">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="m-0 font-weight-bold">Option Items</label>
                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={addOptionItem}>
                      <i className="fas fa-plus mr-1"></i>Add Item
                    </button>
                  </div>
                  {formData.options.length === 0 ? (
                    <div className="text-center py-3" style={{ background: '#f8f9fa', borderRadius: '12px' }}>
                      <p className="text-muted mb-0">No items added yet</p>
                    </div>
                  ) : (
                    <div>
                      {formData.options.map((item, idx) => (
                        <div key={idx} className="row mb-2 align-items-center">
                          <div className="col-md-6">
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Item name"
                              value={item.name}
                              onChange={(e) => updateOptionItem(idx, 'name', e.target.value)}
                            />
                          </div>
                          <div className="col-md-4">
                            <div className="input-group">
                              <div className="input-group-prepend">
                                <span className="input-group-text">$</span>
                              </div>
                              <input
                                type="number"
                                step="0.01"
                                className="form-control"
                                placeholder="Extra fee"
                                value={item.extraFee}
                                onChange={(e) => updateOptionItem(idx, 'extraFee', parseFloat(e.target.value) || 0)}
                              />
                            </div>
                          </div>
                          <div className="col-md-2 text-right">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => removeOptionItem(idx)}
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
                <button type="submit" className="btn btn-primary" style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35' }}>
                  {option ? 'Update' : 'Create'} Option
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Product Selector Modal */}
      {showProductSelector && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1060 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title font-weight-bold">Select Products</h5>
                <button type="button" className="close" onClick={() => setShowProductSelector(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <div className="input-group">
                    <div className="input-group-prepend">
                      <span className="input-group-text"><i className="fas fa-search"></i></span>
                    </div>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search products..."
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      autoFocus
                    />
                    {productSearch && (
                      <div className="input-group-append">
                        <button className="btn btn-outline-secondary" onClick={() => setProductSearch('')}>
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
                  {filteredProducts.length === 0 ? (
                    <div className="text-center py-4">
                      <p className="text-muted">No products found</p>
                    </div>
                  ) : (
                    filteredProducts.map((product) => {
                      const isSelected = formData.usedInProductIds.includes(product.id);
                      return (
                        <div
                          key={product.id}
                          className={`card mb-2 ${isSelected ? 'border-primary' : ''}`}
                          style={{
                            borderRadius: '12px',
                            cursor: 'pointer',
                            border: isSelected ? '2px solid #ff6b35' : '1px solid #e0e0e0',
                          }}
                          onClick={() => toggleProduct(product.id)}
                        >
                          <div className="card-body p-3">
                            <div className="custom-control custom-checkbox">
                              <input
                                type="checkbox"
                                className="custom-control-input"
                                id={`prod-${product.id}`}
                                checked={isSelected}
                                onChange={() => {}}
                              />
                              <label
                                className="custom-control-label font-weight-bold"
                                htmlFor={`prod-${product.id}`}
                                style={{ cursor: 'pointer' }}
                              >
                                {product.name}
                              </label>
                            </div>
                            <small className="text-muted ml-4">{product.categoryName}</small>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowProductSelector(false)}>Done</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductOptionsPage;
